import { useCallback, useEffect, useState } from 'react';

export type DeviceKind = 'audioinput' | 'videoinput' | 'audiooutput';

export interface GroupedDevices {
  audioinput: MediaDeviceInfo[];
  videoinput: MediaDeviceInfo[];
  audiooutput: MediaDeviceInfo[];
}

export interface SavedDevicePreferences {
  audioinput?: string;
  videoinput?: string;
  audiooutput?: string;
}

const STORAGE_KEY = 'callDevicePreferences:v1';
const SKIP_LOBBY_KEY = 'callSkipPreCallLobby:v1';

/**
 * Whether the user has opted out of the pre-call device-check lobby for
 * outbound calls. The setting is intentionally separate from the device
 * preferences object so toggling it never accidentally clobbers a saved
 * mic/camera/speaker selection (and vice versa).
 *
 * Only honoured for outbound `startCall` flows. Accepting an incoming ring
 * always bypasses the lobby — making the caller wait while the callee
 * inspects sliders would be more disruptive than the lobby is helpful.
 */
export function getSkipPreCallLobby(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(SKIP_LOBBY_KEY) === 'true';
  } catch {
    return false;
  }
}

export function saveSkipPreCallLobby(skip: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (skip) localStorage.setItem(SKIP_LOBBY_KEY, 'true');
    else localStorage.removeItem(SKIP_LOBBY_KEY);
  } catch {
    // localStorage may be disabled (private mode etc.) — fail silently.
  }
}

/** Read the user's persisted mic/camera/speaker selection. */
export function getSavedDevicePreferences(): SavedDevicePreferences {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: SavedDevicePreferences = {};
    if (typeof parsed.audioinput === 'string') out.audioinput = parsed.audioinput;
    if (typeof parsed.videoinput === 'string') out.videoinput = parsed.videoinput;
    if (typeof parsed.audiooutput === 'string') out.audiooutput = parsed.audiooutput;
    return out;
  } catch {
    return {};
  }
}

/** Persist (or clear, when deviceId is null) the user's choice for a given device kind. */
export function saveDevicePreference(kind: DeviceKind, deviceId: string | null): void {
  if (typeof window === 'undefined') return;
  const current = getSavedDevicePreferences();
  if (deviceId) {
    current[kind] = deviceId;
  } else {
    delete current[kind];
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    // localStorage may be disabled (private mode etc.) — fail silently.
  }
}

/**
 * Whether `<audio>.setSinkId(...)` is implemented in this browser. Chromium
 * has it; Safari and Firefox do not (as of writing). The speaker dropdown is
 * still rendered on unsupported browsers but disabled with an explanatory
 * tooltip per the product spec.
 */
export function isSpeakerSelectionSupported(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof HTMLMediaElement === 'undefined') return false;
  return 'setSinkId' in HTMLMediaElement.prototype;
}

export async function enumerateMediaDevices(): Promise<MediaDeviceInfo[]> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
    return [];
  }
  try {
    return await navigator.mediaDevices.enumerateDevices();
  } catch {
    return [];
  }
}

export function groupDevices(list: MediaDeviceInfo[]): GroupedDevices {
  return {
    audioinput: list.filter((d) => d.kind === 'audioinput'),
    videoinput: list.filter((d) => d.kind === 'videoinput'),
    audiooutput: list.filter((d) => d.kind === 'audiooutput'),
  };
}

export interface UseMediaDevicesResult {
  devices: GroupedDevices;
  /** True when at least one entry has a non-empty `label` — implies we have
   * media permission and the dropdowns can show real names. */
  hasLabels: boolean;
  speakerSupported: boolean;
  /** Issue a one-shot getUserMedia request to populate device labels. Must
   * only be called from a user gesture (click); calling on mount is
   * anti-spam-blocked by browsers. Safe to call when labels already exist. */
  ensureLabels: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Enumerate input/output devices and re-enumerate on `devicechange`.
 * Without an active media permission, browsers return entries with empty
 * `label` fields — the consumer should call `ensureLabels()` from a user
 * gesture (e.g. opening the device menu) to fill them in.
 */
export function useMediaDevices(active: boolean = true): UseMediaDevicesResult {
  const [list, setList] = useState<MediaDeviceInfo[]>([]);

  const refresh = useCallback(async () => {
    const next = await enumerateMediaDevices();
    setList(next);
  }, []);

  const ensureLabels = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return;
    try {
      // The cheapest possible permission grab — audio-only, immediately stopped.
      // Once any track has been live, subsequent enumerateDevices() calls
      // return populated labels for the rest of the session.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => {
        try { t.stop(); } catch {}
      });
    } catch {
      // Permission denied or no devices — labels stay empty and the dropdown
      // falls back to generic names. Don't toast here; this is invoked from
      // an info-only path (opening a menu).
    }
    await refresh();
  }, [refresh]);

  useEffect(() => {
    if (!active) return;
    void refresh();
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.addEventListener) return;
    const handler = () => { void refresh(); };
    navigator.mediaDevices.addEventListener('devicechange', handler);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handler);
    };
  }, [active, refresh]);

  const devices = groupDevices(list);
  const hasLabels = list.some((d) => d.label.length > 0);

  return {
    devices,
    hasLabels,
    speakerSupported: isSpeakerSelectionSupported(),
    ensureLabels,
    refresh,
  };
}
